import FormDialog from '@/components/ui/form-dialog';
import { FunctionComponent, ReactElement } from 'react';
import { UserResponse } from '@/api/users/types';
import { useDeleteUser } from '@/queries/users';
import { pushToDataLayer, ContentGroup } from '@/shared/utils/analytics';
import useAuth from '@/shared/context/auth-context';
import { useNavigate } from 'react-router-dom';
import { removeAuthData } from '@/shared/utils/auth';

type User = UserResponse;

interface DeleteUserFormProps {
	open: boolean;
	onClose: () => void;
	user?: User;
	onSubmitted?: () => Promise<void> | void;
	customerName?: string;
	isAdminForm?: boolean;
}

const DeleteUserForm: FunctionComponent<DeleteUserFormProps> = ({
	open,
	onClose,
	user,
	onSubmitted,
	customerName,
	isAdminForm,
}): ReactElement => {
	const { user: authUser } = useAuth();
	const navigate = useNavigate();

	const trackRemoveUser = () => {
		pushToDataLayer({
			event: isAdminForm ? 'admin_user_removed' : 'user_removed',
			content_group: isAdminForm ? ContentGroup.Users : ContentGroup.Customers,
			customer_name: customerName,
		});
	};
	const { isLoading: isDeleteUserFormSubmitting, mutate: onDeleteUserFormSubmit } = useDeleteUser({
		onSuccess: async () => {
			const { id: userId } = user || {};
			const { id: authUserId } = authUser || {};
			const deletingThemselves = userId === authUserId;
			if (!deletingThemselves) await onSubmitted?.();
			onClose();
			trackRemoveUser();

			if (deletingThemselves) {
				removeAuthData();
				navigate('/auth/login');
			}
		},
	});

	return (
		<FormDialog
			title="Remove user"
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="xs"
			actions={{
				cancel: { label: 'Cancel' },
				confirm: { label: 'Remove user', isLoading: isDeleteUserFormSubmitting },
			}}
			onSubmit={() => user && onDeleteUserFormSubmit(user.id)}
		>
			{user &&
				`Are you sure you wish to remove user ${user.firstName} ${user.lastName}? This action cannot be undone.`}
		</FormDialog>
	);
};

export default DeleteUserForm;
